import type { BuyerResearchRunSummaryDto } from "@/api/buyerResearch";
import { StatusBadge, Timestamp, Tooltip } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { formatDateTimeInTz } from "@/design-system/lib/format";
import { useUserTimeZone } from "@/utils/datetime";
import { researchCopy } from "./researchCopy";
import { formatCost, runStatusTone } from "./researchFormat";
import { isQuickRun, truncateQuickText } from "./QuickSearchBox";

/**
 * Every run for this buyer, newest first, one selected.
 *
 * <p>Each row carries the facts a specialist asks of a past search without
 * opening it: when, who, how it ended, how many sources answered, how many
 * candidates came back and were shown, and what it cost. The selected row is
 * the one the tab body renders — an `aria-current` row rather than a badge, so
 * the selection is announced as well as tinted.</p>
 */
export function ResearchRunHistory({
  runs,
  selectedId,
  onSelect,
}: {
  runs: BuyerResearchRunSummaryDto[];
  selectedId: string | null;
  onSelect: (runId: string) => void;
}) {
  const tz = useUserTimeZone();
  const copy = researchCopy.history;
  return (
    <div data-testid="research-run-history">
      <h3 className="m-0 mb-space-2 text-caption uppercase tracking-wide text-gray-mid">
        {researchCopy.tab.historyHeading}
      </h3>
      <ul className="m-0 p-0 list-none grid gap-space-1">
        {runs.map((r) => {
          const selected = r.id === selectedId;
          // A quick run names what was typed, cut on a word; the full sentence
          // is in the shared Tooltip (hover AND focus — the row is a button)
          // and in the row's accessible name, never in a native `title`.
          const quickText = isQuickRun(r) ? (r.quickSearch?.text ?? "") : null;
          const opened = copy.open(formatDateTimeInTz(r.queuedAt, tz));
          const row = (
              <button
                type="button"
                onClick={() => onSelect(r.id)}
                aria-current={selected ? "true" : undefined}
                aria-label={quickText ? `${opened} — ${researchCopy.quick.historyLabel(quickText)}` : opened}
                className={cn(
                  "w-full text-left rounded-md border px-space-3 py-space-2 grid gap-[2px] transition-colors duration-fast ease-standard",
                  "focus-visible:outline-none focus-visible:shadow-goldRing",
                  selected
                    ? "border-gold bg-gold/10"
                    : "border-border-light bg-white-pure hover:border-gold",
                )}
                data-testid="research-run-row"
                data-run-id={r.id}
              >
                {quickText && (
                  <span className="text-small font-semibold text-onyx break-words" data-testid="research-run-quick-label">
                    {researchCopy.quick.historyLabel(truncateQuickText(quickText))}
                  </span>
                )}
                <span className="flex flex-wrap items-center gap-space-2 text-small">
                  <Timestamp value={r.queuedAt} tz={tz} className="text-small text-onyx" />
                  <span className="text-gray-mid">{copy.requestedBy(r.requestedByName)}</span>
                  <StatusBadge tone={runStatusTone(r.status)}>
                    {researchCopy.status[r.status] ?? r.status}
                  </StatusBadge>
                  {r.cancelRequested && !r.completedAt && (
                    <span className="text-caption text-gray-mid">{researchCopy.tab.cancelRequested}</span>
                  )}
                </span>
                <span className="flex flex-wrap items-center gap-space-2 text-caption text-gray-mid">
                  <span>{copy.sources(r.sourcesSucceeded, r.sourcesAttempted)}</span>
                  <span>·</span>
                  <span>{copy.candidates(r.candidateCount, r.shownCount)}</span>
                  <span>·</span>
                  <span className="tabular-nums">{formatCost(r.estimatedCostUsd)}</span>
                  {/* D8 — the allowance is spent in RECORDS, not candidates, and the
                      two differ by every row the mapper dropped. The match count is
                      what Clef's free count call said the filter matched, so a
                      capped page reads as capped instead of as a thin market. */}
                  {r.clefRecordsUsed > 0 && (
                    <>
                      <span>·</span>
                      <span className="tabular-nums" data-testid="research-run-clef-records">
                        {copy.clefRecords(r.clefRecordsUsed)}
                      </span>
                    </>
                  )}
                  {r.clefMatchCount !== null && (
                    <>
                      <span>·</span>
                      <span data-testid="research-run-clef-matches">
                        {copy.clefShowingOf(
                          r.shownCount,
                          r.clefMatchCount,
                          r.clefMatchCountApproximate,
                        )}
                      </span>
                    </>
                  )}
                  {r.terminalReason && (
                    <>
                      <span>·</span>
                      <span>
                        {researchCopy.tab.terminalReasonPrefix} {r.terminalReason}
                      </span>
                    </>
                  )}
                </span>
              </button>
          );
          return (
            <li key={r.id}>
              {quickText ? <Tooltip label={researchCopy.quick.historyLabel(quickText)}>{row}</Tooltip> : row}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
